import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Flag, ChevronDown, ChevronUp, Users } from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { STATUSES, decisionBand } from "../../lib/statuses";
import api from "../../lib/api";

const STAT_CARDS = [
  { key: "total", label: "Total Applicants" },
  { key: "awaiting_prescreen", label: "Awaiting Pre-Screen" },
  { key: "in_progress", label: "Pre-Screen In Progress" },
  { key: "awaiting_review", label: "Awaiting Review" },
  { key: "additional_info", label: "Additional Info Needed" },
  { key: "approved", label: "Approved to Advance" },
  { key: "not_selected", label: "Not Selected" },
  { key: "training_in_progress", label: "Training In Progress" },
  { key: "training_completed", label: "Training Completed" },
  { key: "ready_interview", label: "Ready for Interview" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [culture, setCulture] = useState("all");
  const [sort, setSort] = useState({ col: "submitted_at", dir: "desc" });
  const navigate = useNavigate();

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  };
  const loadRows = async () => {
    try {
      const { data } = await api.get("/admin/applicants", { params: { status, search: search || undefined } });
      setRows(data);
    } catch { toast.error("Could not load applicants."); }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { const t = setTimeout(loadRows, 250); return () => clearTimeout(t); }, [status, search]);

  const sorted = useMemo(() => {
    let r = [...rows];
    if (culture !== "all") r = r.filter((x) => (x.culture_alignment || "NOT COMPLETED") === culture);
    r.sort((a, b) => {
      const av = a[sort.col] ?? "", bv = b[sort.col] ?? "";
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return r;
  }, [rows, sort, culture]);

  const toggleSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  const COLS = [
    { key: "applicant_name", label: "Applicant Name" },
    { key: "degree", label: "Degree" },
    { key: "major", label: "Major" },
    { key: "qmhpcs", label: "QMHP-CS" },
    { key: "years_experience", label: "Years Exp." },
    { key: "status", label: "Assessment Status" },
    { key: "score", label: "Score", num: true },
    { key: "qualification_flag", label: "Flag" },
    { key: "culture_alignment", label: "Culture" },
    { key: "reviewer", label: "Reviewer" },
    { key: "submitted_at", label: "Submitted" },
    { key: "training_status", label: "Training" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-[#2C5545]" />
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1F2924] sm:text-3xl">Administrator Dashboard</h1>
        </div>

        {/* Stat cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {STAT_CARDS.map((c) => (
            <div key={c.key} className="rounded-xl border border-[#E5E2DC] bg-white p-4" data-testid={`stat-${c.key}`}>
              <div className="text-2xl font-bold text-[#2C5545] font-heading">{stats[c.key] ?? 0}</div>
              <div className="mt-1 text-xs text-[#515E58]">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#515E58]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="applicant-search"
              placeholder="Search by name, degree or major…" className="bg-white pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full bg-white sm:w-64" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={culture} onValueChange={setCulture}>
            <SelectTrigger className="w-full bg-white sm:w-52" data-testid="culture-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Culture</SelectItem>
              <SelectItem value="ALIGNED">Aligned</SelectItem>
              <SelectItem value="UNSURE">Unsure</SelectItem>
              <SelectItem value="SELF-SELECTED OUT">Self-Selected Out</SelectItem>
              <SelectItem value="NOT COMPLETED">Not Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-xl border border-[#E5E2DC] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="applicant-table">
              <thead>
                <tr className="border-b border-[#E5E2DC] bg-[#F4F2EC]">
                  {COLS.map((c) => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}
                      className={`cursor-pointer whitespace-nowrap px-4 py-3 font-medium text-[#515E58] ${c.num ? "text-right" : "text-left"}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sort.col === c.key && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const band = decisionBand(r.score);
                  return (
                    <tr key={r.id} onClick={() => navigate(`/admin/applicants/${r.id}`)}
                      className="cursor-pointer border-b border-[#E5E2DC] last:border-0 hover:bg-[#F4F2EC]"
                      data-testid={`applicant-row-${r.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-[#1F2924]">{r.applicant_name || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.degree || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.major || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.qmhpcs || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.years_experience || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-[#1F2924]">
                        {r.score != null ? <span title={band?.label}>{r.score}</span> : "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.qualification_flag && <Flag className="mx-auto h-4 w-4 text-[#E89B31]" />}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {r.culture_alignment && r.culture_alignment !== "NOT COMPLETED" ? (
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${r.culture_alignment === "ALIGNED" ? "border-[#3A7D5E]/30 bg-[#EAF3EE] text-[#2C5545]" : r.culture_alignment === "UNSURE" ? "border-[#E89B31]/40 bg-[#FDF6EA] text-[#B4791F]" : "border-[#C44F4F]/30 bg-[#FBEDED] text-[#C44F4F]"}`}>
                            {r.culture_alignment === "SELF-SELECTED OUT" ? "Self-Selected Out" : r.culture_alignment === "ALIGNED" ? "Aligned" : "Unsure"}
                          </span>
                        ) : <span className="text-[#515E58]">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.reviewer || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">
                        {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-[#515E58]">{r.training_status}</td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr><td colSpan={COLS.length} className="px-4 py-10 text-center text-[#515E58]">No applicants found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
